import './MarketPicker.scss';
import React, {ChangeEvent, useCallback, useEffect, useMemo, useRef, useState} from "react";
import classnames from "classnames";
import {RootMarketNodes} from "@/types/ApiTypes"
import {
    isMarketDisabled, 
    useMarketService,     
    filterTreeByVisibleDescendants,
    type MarketVisibilityContext
} from "@/services/market.service";
import {ByzzerChangeEventHandler, ByzzerTipIcon} from "@byzzer/ui-components";
import {noop, uniq} from "lodash";
import {MarketPickerNode} from "./MarketPickerNode";
import {
    MarketPickerContext,
    MarketPickerContextValue
} from "./MarketPickerContext";
import { ReportDatatype, RunConfigMarket } from '@/types/ReportRun';
import { getRegexForSearchString } from '@/utils';
import { useUser } from '@/contexts/UserContext';
import { invertedMarketFolderLabelMapping, marketFolderLabelMapping } from '../DodConfigEditor/common/utils';
import _ from 'lodash';

export type MarketPickerProps = {
    productSku?: string;
    className?: string;
    categories?: string[];
    maxSelections?: number;
    requireRemainingMarket?: boolean;
    requireRemainingMarketSuffix?: string;
    requiredMasterCompany?: string;
    requiredMarketGroup?: string;
    runType?: RunType;
    value?: RunConfigMarket[];
    name?: string;
    onChange?: ByzzerChangeEventHandler<RunConfigMarket[]>;
    hideTotalUSMarkets?: boolean;
    showRemainingMarkets?: boolean;
    hideDisabledMarkets?: boolean;
    hideNonPromoMarkets?: boolean;
    displayMarketGroup: ReportDatatype;
    includePseudoRemainingMarkets?: boolean;
    hideGeographicMarkets?: boolean;
    purchasedMarketKeys?: string[];
    isDodMarketPicker?: boolean;
    filterText?: string;
    hasSummedCategories?:boolean;
    summedCategories?:string[];
    nonSummedCategories?:string[];
    isMarketSummedMode?:boolean;
    enableParentMarket?: boolean;
    filterType?: string;
    updateAccessibleMarketFolders?: (folders) => void;
    isParentAndChildMarketSelectionRequired?: boolean;
    onCompanyHasOnlyL1MarketsCheck?: (masterCompany: string, hasOnlyL1: boolean, path: string) => void;
};

const baseClassName = 'market-picker';
export function MarketPicker(
    {
        className,
        productSku,
        categories=[], // selected categories
        onChange,
        value,
        name,
        runType = 'subscription',
        maxSelections,
        requiredMasterCompany,
        requiredMarketGroup,
        requireRemainingMarket,
        requireRemainingMarketSuffix,
        hideTotalUSMarkets,
        showRemainingMarkets,
        hideDisabledMarkets,
        hideNonPromoMarkets,
        includePseudoRemainingMarkets = false,
        displayMarketGroup,
        hideGeographicMarkets = false,
        purchasedMarketKeys,
        isDodMarketPicker = false,
        hasSummedCategories = false,
        summedCategories,
        nonSummedCategories,
        filterText,
        isMarketSummedMode,
        enableParentMarket,
        updateAccessibleMarketFolders,
        filterType,
        isParentAndChildMarketSelectionRequired = false,
        onCompanyHasOnlyL1MarketsCheck,
    }: MarketPickerProps) {
    const {
        features,
        company,
        features: { enableLimitedMarketSubscription },
        accessibleMasterCompanies,
    } = useUser();
    const {
        getRootMarketNodes,
        getChildMarketNodes,
        getCachedMarketNodeByName,
        getAccessibleRootMarketNodes,
        getEntireMarketTree,
    } = useMarketService();
    const [rootMarketNodes, setRootMarketNodes] = useState<RootMarketNodes | undefined>();
    const [filteredRootMarketNodes, setFilteredRootMarketNodes] = useState<typeof rootMarketNodes>(rootMarketNodes);
    const [lastClickedPath, setLastClickedPath] = useState<string | null>(null);
    const [selectableMarkets, setSelectableMarkets] = useState<MarketNode[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [visibleMarkets, setVisibleMarkets] = useState<MarketNode[]>([]); // For shift-to-select functionality
    
    const [contextValue, setContextValue] = useState<MarketPickerContextValue>({
        addMarket: noop,
        removeMarket: noop,
        getChildMarketNodes(parent) {
            return getChildMarketNodes({
                parent,
                sku: productSku!,
                categories, //PR review candidate
                runType,
                purchasedMarketKeys,
                isDodMarketPicker,
                hasSummedCategories,
                summedCategories,
                nonSummedCategories,
                isMarketSummedMode,
                isEnableParentCompanyMode: enableParentMarket
            });
        },
        selectedMarkets: [],
        selectedMasterCompanies: [],
        maxSelections: Number(maxSelections) > 0 ? maxSelections : Infinity,
        showRemainingMarkets,
        hideDisabledMarkets,
        hideNonPromoMarkets,
        requireRemainingMarket,
        requireRemainingMarketSuffix,
        requiredMasterCompany,
        requiredMarketGroup,
        onShiftSelect: noop,
        enableParentMarket
    });

    const enableAdvancedShiftSelect = maxSelections != null && maxSelections !== Infinity && maxSelections > 1;

    // BYZ-12613 to avoid duplicate child markets being shown on ui, returned by getAccessibleMarketNodes() method below

    {
        /** BYZ-12613 little context here: in byzzer, when enableLimitedMarket flag is set to true, user will have access to only accessible markets
         *
         * getAccessibleRootMarketNodes() method used above, basically returns all accessible markets (including both parent and its child markets)
         * which the user has access to based on their category selection as an array of marketNodes
         *
         * since the api is designed in a way to return both parent and child markets, jsx was showing expand collapse icon for each parent market
         * which when expanded, again shows the child markets in addition to the child market already returned by the api
         *
         * Hence the child markets were getting duplicated all over the place for limited market user.
         *
         * since its purely a ui enhancement in MarketPicker component to display all markets in a flat list,
         * we format the data and render only the parent market node without expand/collapse icon
         * hence we pass each accessible marketNode through formatAccessibleMarket() method before rendering
         */
    }
    // Memoize formatted accessible markets to prevent new object references on every render
    const formattedAccessibleMarkets = useMemo(() => {
        if (!filteredRootMarketNodes?.accessible) return [];
        return filteredRootMarketNodes.accessible.map(market => ({
            ...market,
            hasChildren: false
        }));
    }, [filteredRootMarketNodes?.accessible]);

    const addMarket = useCallback((market: MarketNode, remainingMarketName?: string) => {
        const currentSelectedMarket = (value ?? [])?.find(
            (currentMarket) => currentMarket?.path === market?.path
        );
        const otherSelectedMarkets = (value ?? [])?.filter(
            (currentMarket) => currentMarket?.path !== market?.path
        );

        const remainingMarketRunConfig = getCachedMarketNodeByName(remainingMarketName);
        let remainingMarket: RunConfigMarket | undefined;

        let selectedMarkets: RunConfigMarket[] = [];
        const limit = Number(maxSelections) > 0 ? maxSelections : Infinity;
        let newMarket: RunConfigMarket = {
            path: market.path,
            key: market.key,
            name: market.name,
            selectionType: market?.selectionType,
            masterCompany: market.masterCompany,
            marketGroup: market.marketGroup,
            marketHasPartialApproval: market.hasPartialApproval,
            isParentCompany: market?.isParentCompany,
            isOutletOfParentCompany: market?.isOutletOfParentCompany
        };

        if (remainingMarketRunConfig) {
            remainingMarket = {
                isRemaining: true,
                path: remainingMarketRunConfig.path,
                key: remainingMarketRunConfig.key,
                name: remainingMarketRunConfig.name,
                selectionType: remainingMarketRunConfig?.selectionType,
                masterCompany: remainingMarketRunConfig.masterCompany,
                marketGroup: remainingMarketRunConfig.marketGroup,
                marketHasPartialApproval: market.hasPartialApproval,
            };
            (currentSelectedMarket ?? newMarket).remainingMarketRunConfig = remainingMarket;
        }

        if (limit === 1) {
            selectedMarkets = [currentSelectedMarket ?? newMarket];
        } else {
            selectedMarkets = [...otherSelectedMarkets, currentSelectedMarket ?? newMarket]
                .filter(Boolean)
                .slice(0, limit);
        }
        onChange?.({
            name,
            value: selectedMarkets,
        });
    }, [value, maxSelections, name, onChange, getCachedMarketNodeByName]);

    const removeMarket = useCallback((marketToRemove: MarketNode, includeRemaining?: boolean) => {
        const selectedMarkets = (value ?? []).filter((market) => {
            return (
                market.name !== marketToRemove.name &&
                (!includeRemaining || !marketToRemove.remainingMarketNames?.includes(market.name))
            );
        });
        onChange?.({
            name,
            value: selectedMarkets,
        });
    }, [value, name, onChange]);

    // Track visible markets for shift-select
    const registerVisibleMarket = useCallback((market: MarketNode) => {
        setVisibleMarkets((prev) => {
            if (enableAdvancedShiftSelect) {
                // For advanced shift-select: maintain visual display order (parent, then children)
                const exists = prev.some(m => m.path === market.path);
                if (exists) {
                    return prev;
                }
                return [...prev, market];
            } else {
                // For basic shift-select: original behavior - remove if exists and re-add with sorting
                const filtered = prev.filter(m => m.path !== market.path);
                return [
                    ...filtered, 
                    market
                ].toSorted((a, b) => a.path.localeCompare(b.path));
            }
        });
    }, [enableAdvancedShiftSelect]);

    // Unregister visible market on unmount or when no longer visible for shift-to-select
    const unregisterVisibleMarket = useCallback((path: string) => {
        setVisibleMarkets((prev) => prev.filter(marketNode => marketNode.path !== path));
    }, []);

    const onShiftSelect = useCallback((currentSelectedPath: string) => {
        let marketData = _.cloneDeep(visibleMarkets);
        const shouldUseAccessibleMarkets = features?.enableLimitedMarketSubscription;
        if (shouldUseAccessibleMarkets) {
            marketData = filteredRootMarketNodes?.accessible || [];
        }
        let startIndex = marketData.findIndex(val => val.path === lastClickedPath);
        let endIndex = marketData.findIndex(val => val.path === currentSelectedPath);
        if(startIndex === -1 || endIndex === -1) return;
        let indices = [startIndex,endIndex];
        indices.sort((a,b) => a - b);
        [startIndex,endIndex] = indices;
        const otherSelectedMarkets = (value ?? [])?.filter(
            (currentMarket) => !marketData?.slice(startIndex, endIndex + 1).some((childMarket) => childMarket.path === currentMarket.path )
        );
        const selectedMarkets = marketData?.slice(startIndex, endIndex + 1).reduce((accum: RunConfigMarket[], market) => {
            const marketIsDisabled = isMarketDisabled(market, {
                requiredMarketGroup,
                requiredMasterCompany,
                requireRemainingMarket,
                enableLimitedMarketSubscription,
                accessibleMasterCompanies,
                reportType: displayMarketGroup,
                purchasedMarketKeys: company?.purchasedMarketKeys,
            });

            if (market.selectable && !marketIsDisabled && (showRemainingMarkets || !market.isRemaining)) {
                let newMarket: RunConfigMarket = {
                    path: market.path,
                    key: market.key,
                    name: market.name,
                    selectionType: market?.selectionType,
                    masterCompany: market.masterCompany,
                    marketGroup: market.marketGroup,
                    marketHasPartialApproval: market.hasPartialApproval,
                    isParentCompany: market?.isParentCompany,
                    isOutletOfParentCompany: market?.isOutletOfParentCompany
                };
                return [...accum, newMarket];
            }
            return accum;
        }, [])
        
        let finalSelectedMarkets = selectedMarkets;
        
        // Apply advanced filtering only if enableAdvancedShiftSelect flag is true
        if (enableAdvancedShiftSelect) {
            // Filter out children whose parents are in the selection, unless they were already selected
            finalSelectedMarkets = selectedMarkets.filter(market => {
                // Check if this market's parent is also in selectedMarkets
                const isChildOfSelectedParent = selectedMarkets.some(otherMarket => {
                    if (market.path === otherMarket.path) return false;
                    // Treat another market as a parent only if this market's path starts with
                    // the other market's path followed by a path separator.
                    return market.path.startsWith(otherMarket.path + '/');
                });
                
                if (isChildOfSelectedParent) {
                    // Exclude this child unless it was already selected
                    return (value ?? []).some(m => m.path === market.path);
                }
                return true;
            });
        }
        
        const limit = enableAdvancedShiftSelect && Number(maxSelections) > 0 ? maxSelections : Infinity;
        const combinedMarkets = uniq([...finalSelectedMarkets, ...(otherSelectedMarkets ?? [])]);
        const finalMarkets = combinedMarkets.slice(0, limit);
        
        setTimeout(() => {
            onChange?.({
                name,
                value: finalMarkets,
            });
        }, 100); 
    }, [
        visibleMarkets,
        filteredRootMarketNodes,
        lastClickedPath,
        value,
        maxSelections,
        enableAdvancedShiftSelect,
        requiredMarketGroup,
        requiredMasterCompany,
        requireRemainingMarket,
        enableLimitedMarketSubscription,
        accessibleMasterCompanies,
        displayMarketGroup,
        company?.purchasedMarketKeys,
        showRemainingMarkets,
        name,
        onChange,
        features?.enableLimitedMarketSubscription
    ]);

    const selectedMarketPaths = useMemo(() => 
        new Set((value ?? []).map(m => m.path)), 
        [value]
    );

    // Remove the entire second useEffect and replace with useMemo
    const contextValueMemo = useMemo(() => ({
        addMarket,
        removeMarket,
        getChildMarketNodes: async (parent: MarketNode) => Promise.resolve(parent.children || []),
        selectedMarkets: value ?? [],
        selectedMarketPaths, // Add this for cheaper lookups
        selectedMasterCompanies: [],
        maxSelections: Number(maxSelections) > 0 ? maxSelections : Infinity,
        showRemainingMarkets,
        hideDisabledMarkets,
        hideNonPromoMarkets,
        requireRemainingMarket,
        requireRemainingMarketSuffix,
        requiredMasterCompany,
        requiredMarketGroup,
        includePseudoRemainingMarkets,
        onShiftSelect,
        setLastClickedPath,
        lastClickedPath,
        setSelectableMarkets,
        selectableMarkets,
        enableParentMarket,

        // Tracking visible markets for shift-to-select
        visibleMarkets, registerVisibleMarket, unregisterVisibleMarket
    }), [
        addMarket,
        removeMarket,
        onShiftSelect,
        selectedMarketPaths, // ← Use the Set instead of value
        maxSelections,
        showRemainingMarkets,
        hideDisabledMarkets,
        hideNonPromoMarkets,
        requireRemainingMarket,
        requireRemainingMarketSuffix,
        requiredMasterCompany,
        requiredMarketGroup,
        includePseudoRemainingMarkets,
        lastClickedPath,
        selectableMarkets,
        enableParentMarket,
        enableAdvancedShiftSelect,

        // Tracking visible markets for shift-to-select
        registerVisibleMarket, unregisterVisibleMarket
    ]);

    useEffect(() => {
        if (!productSku) return;

        (async () => {
            try {
                setLoading(true);
                let rootMarkets: RootMarketNodes;
                const shouldUseAccessibleMarkets = features?.enableLimitedMarketSubscription;
                const rootMarketKeyOrder: Partial<keyof RootMarketNodes>[] = ['totalUs', 'fmcgRetailers', 'specialityRetailers', 'geographies'];
                let allMarkets: MarketNode[] = [];
                if (shouldUseAccessibleMarkets) {
                    setLoading(true)
                    rootMarkets = await getAccessibleRootMarketNodes({sku: productSku, categories, runType, purchasedMarketKeys, requiresAllCategoriesApproved: !isDodMarketPicker}); // requiresAllCategoriesApproved is false if request is from DOD
                    
                    allMarkets = rootMarkets?.accessible ?? [];
                    // Extract unique sub_market_types (folder names)
                    const uniqueSubMarketTypes = [
                        ...new Set(
                            allMarkets
                                .map((market) => market.subMrktType)
                                .filter(Boolean)
                        ),
                    ];

                    // Build accessible market folders
                    const accessibleMarketFolders = uniqueSubMarketTypes.map((subMarketType) => {
                        const mappedKey = marketFolderLabelMapping[subMarketType] || subMarketType;
                        const formattedLabel = subMarketType.charAt(0).toUpperCase() + subMarketType.slice(1) + 's';
                        
                        return {
                            key: mappedKey,
                            label: formattedLabel,
                            value: mappedKey,
                        };
                    });
                    updateAccessibleMarketFolders?.([
                        {
                            key: 'all',
                            label: 'All',
                            value: { type: 'all' },
                        }, ...accessibleMarketFolders.map(folder => ({ ...folder, value: { type: folder.value } }))]);
                } else {
                    rootMarkets = await getEntireMarketTree({
                        sku: productSku,
                        categories,
                        runType,
                        purchasedMarketKeys,
                        isDodMarketPicker,
                        hasSummedCategories,
                        summedCategories,
                        nonSummedCategories,
                        isMarketSummedMode,
                        isEnableParentCompanyMode: enableParentMarket
                    });

                    // Build visibility context for filtering
                    const visibilityContext: MarketVisibilityContext = {
                        includePseudoRemainingMarkets,
                        showRemainingMarkets,
                        hideDisabledMarkets,
                        hideNonPromoMarkets,
                        requiredMarketGroup,
                        requiredMasterCompany,
                        requireRemainingMarket,
                        enableLimitedMarketSubscription: features?.enableLimitedMarketSubscription,
                        accessibleMasterCompanies,
                        purchasedMarketKeys,
                    };

                    // Filter each root category to remove nodes without visible descendants
                    const filteredRootMarkets: RootMarketNodes = {};
                    
                    if (rootMarkets.totalUs?.length) {
                        const filtered = filterTreeByVisibleDescendants(rootMarkets.totalUs, visibilityContext);
                        if (filtered.length > 0) {
                            filteredRootMarkets.totalUs = filtered;
                        }
                    }
                    
                    if (rootMarkets.fmcgRetailers?.length) {
                        const filtered = filterTreeByVisibleDescendants(rootMarkets.fmcgRetailers, visibilityContext);
                        if (filtered.length > 0) {
                            filteredRootMarkets.fmcgRetailers = filtered;
                        }
                    }
                    
                    if (rootMarkets.specialityRetailers?.length) {
                        const filtered = filterTreeByVisibleDescendants(rootMarkets.specialityRetailers, visibilityContext);
                        if (filtered.length > 0) {
                            filteredRootMarkets.specialityRetailers = filtered;
                        }
                    }
                    
                    if (rootMarkets.geographies?.length) {
                        const filtered = filterTreeByVisibleDescendants(rootMarkets.geographies, visibilityContext);

                        if (filtered.length > 0) {
                            filteredRootMarkets.geographies = filtered;
                        }
                    }

                    if (rootMarkets.panelTotal?.length) {
                        const filtered = filterTreeByVisibleDescendants(rootMarkets.panelTotal, visibilityContext);
                        if (filtered.length > 0) {
                            filteredRootMarkets.panelTotal = filtered;
                        }
                    }

                    if (rootMarkets.panelChannels?.length) {
                        const filtered = filterTreeByVisibleDescendants(rootMarkets.panelChannels, visibilityContext);
                        if (filtered.length > 0) {
                            filteredRootMarkets.panelChannels = filtered;
                        }
                    }                    

                    rootMarkets = filteredRootMarkets;

                    // Flatten the tree to get all markets for shift-select functionality
                   const flattenNodes = (nodes: MarketNode[]): MarketNode[] => {
                        const result: MarketNode[] = [];
                        const stack: MarketNode[] = [...nodes].reverse(); // Start with reversed input
                        
                        while (stack.length > 0) {
                            const node = stack.pop()!;
                            result.push(node);
                            
                            if (node.children && node.children.length > 0) {
                                // Add children in reverse order to preserve original order
                                for (let i = node.children.length - 1; i >= 0; i--) {
                                    stack.push(node.children[i]);
                                }
                            }
                        }
                        
                        return result;
                    };

                    rootMarketKeyOrder.forEach((key) => {
                        if (rootMarkets[key]) {
                            allMarkets.push(...flattenNodes(rootMarkets[key]!));
                        }
                    });
                }
                
                setSelectableMarkets(allMarkets);
                setRootMarketNodes(rootMarkets);
            } catch(err) {
                // TBD
                console.error(err);
            }finally {
                setLoading(false);
            }
        })();

    }, [productSku, categories.join(','), showRemainingMarkets]);

    useEffect(() => {
        if (rootMarketNodes) {
            const shouldUseAccessibleMarkets = features?.enableLimitedMarketSubscription;
            let filteredMarkets: Partial<Record<keyof RootMarketNodes, MarketNode[]>> = { ...rootMarketNodes };
            if (shouldUseAccessibleMarkets && filterType && filterType !== 'all') {
                filteredMarkets = filterMarketsByType(filteredMarkets, filterType);
            }
            if (filterText) {
                filteredMarkets = filterMarketsByText(filteredMarkets, filterText);
            }
            setFilteredRootMarketNodes(filteredMarkets);
        }
    }, [rootMarketNodes, filterText, filterType, features?.enableLimitedMarketSubscription]);

    // Filtering Functions
    const filterMarketsByType = (
        markets: Partial<Record<keyof RootMarketNodes, MarketNode[]>>,
        filterType: string
    ): Partial<Record<keyof RootMarketNodes, MarketNode[]>> => {
        return Object.entries(markets).reduce((acc, [key, value]) => {
            if (value) {
                const filteredByFolder = value.filter(
                    (market) => market.subMrktType === invertedMarketFolderLabelMapping[filterType]
                );
                if (filteredByFolder.length > 0) {
                    acc[key as keyof RootMarketNodes] = filteredByFolder;
                }
            }
            return acc;
        }, {} as Partial<Record<keyof RootMarketNodes, MarketNode[]>>);
    };

    const filterMarketsByText = (
        markets: Partial<Record<keyof RootMarketNodes, MarketNode[]>>,
        filterText: string
    ): Partial<Record<keyof RootMarketNodes, MarketNode[]>> => {
        const matcher = getRegexForSearchString(filterText);
        return Object.entries(markets).reduce((acc, [key, value]) => {
            if (value) {
                const filteredByText = value.filter(({ name }) =>
                    matcher.test(name.trim())
                );
                if (filteredByText.length > 0) {
                    acc[key as keyof RootMarketNodes] = filteredByText;
                }
            }
            return acc;
        }, {} as Partial<Record<keyof RootMarketNodes, MarketNode[]>>);
    };
    return (
        <div className={classnames(baseClassName, className)}>
            <MarketPickerContext.Provider value={contextValueMemo}>
                {loading && <div className={`${baseClassName}__loading`}>Loading Markets</div>}

                {displayMarketGroup === 'cps' && (
                    <>
                        {formattedAccessibleMarkets.map((market) => (
                            <MarketPickerNode
                                key={market.path}
                                marketNode={market}
                                includeToggle={market.hasChildren}
                                includeSelector={market.selectable}                                
                                displayMarketGroup={displayMarketGroup} // was removed previously after being added.  See BYZ-12783 and BYZ-12921
                            />
                        ))}
                        {Boolean(filteredRootMarketNodes?.panelTotal?.length) && (
                            <h2 className={`${baseClassName}__market-group-title`}>Total US Markets</h2>
                        )}
                        {filteredRootMarketNodes?.panelTotal?.map((market) => (
                            <MarketPickerNode
                                key={market.path}
                                marketNode={market}
                                includeToggle={market.hasChildren}
                                includeSelector={market.selectable}
                                displayMarketGroup={displayMarketGroup}
                            />
                        ))}
                        {Boolean(filteredRootMarketNodes?.panelChannels?.length) && (
                            <h2 className={`${baseClassName}__market-group-title`}>Outlets By Channel</h2>
                        )}
                        {filteredRootMarketNodes?.panelChannels?.map((market) => (
                            <>
                                <MarketPickerNode
                                    key={market.path}
                                    marketNode={market}
                                    includeToggle={market.hasChildren}
                                    includeSelector={market.selectable}
                                    displayMarketGroup={displayMarketGroup}
                                />
                            </>
                        ))}
                    </>
                )}

                {displayMarketGroup === 'rms' && (
                    <>
                        {formattedAccessibleMarkets.map((market) => (
                            <MarketPickerNode
                                key={market.path}
                                marketNode={market}
                                includeToggle={market.hasChildren}
                                includeSelector={market.selectable}
                                displayMarketGroup={displayMarketGroup}
                                isParentAndChildMarketSelectionRequired={isParentAndChildMarketSelectionRequired}
                                onCompanyHasOnlyL1MarketsCheck={onCompanyHasOnlyL1MarketsCheck}
                            />
                        ))}
                        {hideTotalUSMarkets !== true && (
                            <>
                                {Boolean(filteredRootMarketNodes?.totalUs?.length) && (
                                    <h2 className={`${baseClassName}__market-group-title`}>Total US Markets</h2>
                                )}
                                {filteredRootMarketNodes?.totalUs?.map((market) => (
                                    <MarketPickerNode
                                        key={market.path}
                                        marketNode={market}
                                        includeToggle={market.hasChildren}
                                        includeSelector={market.selectable}
                                        displayMarketGroup={displayMarketGroup}
                                        isParentAndChildMarketSelectionRequired={isParentAndChildMarketSelectionRequired}
                                        onCompanyHasOnlyL1MarketsCheck={onCompanyHasOnlyL1MarketsCheck}
                                    />
                                ))}
                            </>
                        )}

                        {Boolean(filteredRootMarketNodes?.fmcgRetailers?.length) && (
                            <h2 className={`${baseClassName}__market-group-title`}>FMCG Retailers By Channel</h2>
                        )}
                        {filteredRootMarketNodes?.fmcgRetailers?.map((market) => (
                            <MarketPickerNode
                                key={market.path}
                                marketNode={market}
                                includeToggle={market.hasChildren}
                                includeSelector={market.selectable}
                                displayMarketGroup={displayMarketGroup}
                                isMarketSummedMode={isMarketSummedMode}
                                isParentAndChildMarketSelectionRequired={isParentAndChildMarketSelectionRequired}
                                onCompanyHasOnlyL1MarketsCheck={onCompanyHasOnlyL1MarketsCheck}
                            />
                        ))}
                        {Boolean(filteredRootMarketNodes?.specialityRetailers?.length) && (
                            <h2 className={`${baseClassName}__market-group-title`}>Specialty Retailers By Channel</h2>
                        )}
                        {filteredRootMarketNodes?.specialityRetailers?.map((market) => (
                            <MarketPickerNode
                                key={market.path}
                                marketNode={market}
                                includeToggle={market.hasChildren}
                                includeSelector={market.selectable}
                                displayMarketGroup={displayMarketGroup}
                                isParentAndChildMarketSelectionRequired={isParentAndChildMarketSelectionRequired}
                                onCompanyHasOnlyL1MarketsCheck={onCompanyHasOnlyL1MarketsCheck}
                            />
                        ))}

                        {hideGeographicMarkets !== true && (
                          <>
                            {Boolean(filteredRootMarketNodes?.geographies?.length) && (
                                <h2 className={`${baseClassName}__market-group-title`}>Geographies</h2>
                            )}
                            {filteredRootMarketNodes?.geographies?.map((market) => (
                                <MarketPickerNode
                                    key={market.path}
                                    marketNode={market}
                                    includeToggle={market.hasChildren}
                                    includeSelector={market.selectable}
                                    displayMarketGroup={displayMarketGroup}
                                    isParentAndChildMarketSelectionRequired={isParentAndChildMarketSelectionRequired}
                                    onCompanyHasOnlyL1MarketsCheck={onCompanyHasOnlyL1MarketsCheck}
                                />
                            ))}
                          </>
                        )}
                    </>
                )}

                {(displayMarketGroup === 'omni' || displayMarketGroup === 'osh') && <></>}
            </MarketPickerContext.Provider>
        </div>
    );
}

export default MarketPicker;
